import { useRecoilCallback, useRecoilValue } from 'recoil';
import type { HangingProtocol } from './useHangingProtocol';
import {
  isPreviewingHangingProtocolState,
  previewHangingProtocolSelector,
  previousHangingProtocolState,
} from './state';
import { useCurrentCaseId } from 'hooks/useCurrentCase';
import { client } from 'modules/Apollo/client';
import { GET_HANGING_PROTOCOL } from 'modules/Apollo/queries';
import { useState } from 'react';
import { usePreviousHangingProtocol } from './usePreviousHangingProtocol';
import { useExamGroupedPriorStudies, usePriorStudyGroups } from './useStudiesGroupedAsCases';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags/suspense';
import { WithApolloContext } from 'modules/Apollo/types';
import { logger } from 'modules/logger';

export type PreviewHangingProtocol = (id: string) => Promise<void>;

export type UsePreviewHangingProtocol = {
  previewHangingProtocol: PreviewHangingProtocol;
  clearPreviewState: () => void;
  rejectAndRemovePreview: () => void;
  loadingPreview: boolean;
};

export const usePreviewHangingProtocolConfiguration = (): Nullable<HangingProtocol> => {
  const currentCaseSmid = useCurrentCaseId();
  return useRecoilValue(previewHangingProtocolSelector(currentCaseSmid));
};

export const usePreviewHangingProtocol = ({
  context,
}: WithApolloContext): UsePreviewHangingProtocol => {
  const enableExamGrouping = useFeatureFlagEnabled(FF.EXAM_GROUPED_HANGING_PROTOCOLS);
  const studiesGroupedByExam = useExamGroupedPriorStudies({ context });
  const ungroupedStudies = usePriorStudyGroups({ context });
  const cases = enableExamGrouping ? studiesGroupedByExam : ungroupedStudies;
  const { previousHangingProtocol, savePreviousHangingProtocol } = usePreviousHangingProtocol();

  const [loadingPreview, setLoadingPreview] = useState(false);

  const currentCaseSmid = useCurrentCaseId();

  const previewHangingProtocol = useRecoilCallback(
    ({ set, snapshot }) => {
      return async (id: string) => {
        if (currentCaseSmid == null || cases.length === 0) return;
        setLoadingPreview(true);
        const isAlreadyPreviewing = snapshot
          .getLoadable(isPreviewingHangingProtocolState)
          .getValue();
        if (!isAlreadyPreviewing) {
          savePreviousHangingProtocol();
          set(isPreviewingHangingProtocolState, true);
        }

        try {
          const { data } = await client.query({
            query: GET_HANGING_PROTOCOL,
            variables: { id, cases },
          });
          const hangingProtocol = data?.hangingProtocol;

          logger.info(`Previewed Hanging Protocol ${hangingProtocol?.smid}`);

          set(previewHangingProtocolSelector(currentCaseSmid), hangingProtocol);
          setLoadingPreview(false);
        } catch (error: any) {
          logger.error('Failed to fetch hanging protocol', error);
          setLoadingPreview(false);
        }
      };
    },
    [cases, currentCaseSmid, savePreviousHangingProtocol]
  );

  const clearPreviewState = useRecoilCallback(
    ({ set, reset }) =>
      () => {
        // Clear preview-specific state; viewport/layout resets are handled by resetToHP in useHangingProtocol
        set(isPreviewingHangingProtocolState, false);
        reset(previousHangingProtocolState);
      },
    []
  );

  const rejectAndRemovePreview = useRecoilCallback(
    ({ set, reset }) =>
      () => {
        if (currentCaseSmid == null) return;
        set(isPreviewingHangingProtocolState, false);

        if (previousHangingProtocol) {
          set(previewHangingProtocolSelector(currentCaseSmid), previousHangingProtocol);
          return;
        }

        reset(previewHangingProtocolSelector(currentCaseSmid));
      },
    [currentCaseSmid, previousHangingProtocol]
  );

  return {
    previewHangingProtocol,
    clearPreviewState,
    rejectAndRemovePreview,
    loadingPreview,
  };
};
